// frontend/src/components/RichTextEditor/RichTextEditor.tsx
import { useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Popover,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  Box,
  Progress,
  Image as MantineImage,
  ThemeIcon,
  Slider
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconH1,
  IconH2,
  IconH3,
  IconList,
  IconListNumbers,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignJustified,
  IconLink,
  IconLinkOff,
  IconPhoto,
  IconTable,
  IconTableMinus,
  IconRowInsertTop,
  IconRowInsertBottom,
  IconColumnInsertLeft,
  IconColumnInsertRight,
  IconTrash,
  IconHighlight,
  IconTextColor,
  IconQuote,
  IconSeparator,
  IconClearFormatting,
  IconPhotoPlus,
  IconLayoutGrid,
  IconUpload,
  IconX,
  IconCheck,
  IconSettings,
  IconArrowsMaximize,
  IconLayoutAlignLeft,
  IconLayoutAlignCenter,
  IconLayoutAlignRight,
  IconTextCaption,
  IconResize
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import contentBlocksApi from '@/api/contentBlocks.api';
import { notifications } from '@mantine/notifications';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const COLOR_PRESETS = [
  '#000000', '#434343', '#666666', '#999999', '#cccccc', '#efefef', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#9900ff',
  '#ff00ff', '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#d9d2e9'
];

// Custom Image extension with width and alignment support
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: element => element.getAttribute('data-width') || element.style.width?.replace('%', '') || null,
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return {
            'data-width': attributes.width,
            style: `width: ${attributes.width}%`
          };
        }
      },
      alignment: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-alignment') || 'center',
        renderHTML: attributes => {
          return {
            'data-alignment': attributes.alignment || 'center'
          };
        }
      }
    };
  }
});

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const { t } = useTranslation();
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);
  const [imageModalOpened, { open: openImageModal, close: closeImageModal }] = useDisclosure(false);
  const [tableModalOpened, { open: openTableModal, close: closeTableModal }] = useDisclosure(false);
  const [galleryModalOpened, { open: openGalleryModal, close: closeGalleryModal }] = useDisclosure(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [galleryImages, setGalleryImages] = useState<{ url: string; width: number }[]>([]);
  const [galleryColumns, setGalleryColumns] = useState(3);

  // Image editing state
  const [imageSelected, setImageSelected] = useState(false);
  const [imageWidth, setImageWidth] = useState<number>(100);
  const [imageAlignment, setImageAlignment] = useState<string>('center');
  const [imageAlt, setImageAlt] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      CustomImage.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
        }
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'editor-table'
        }
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true
      }),
      Placeholder.configure({
        placeholder: placeholder || t('properties.contentBlocks.editor.placeholder') || 'Start writing...'
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link'
        }
      }),
      Underline
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      const isImg = editor.isActive('image');
      setImageSelected(isImg);
      if (isImg) {
        const attrs = editor.getAttributes('image');
        setImageWidth(attrs.width ? Number(attrs.width) : 100);
        setImageAlignment(attrs.alignment || 'center');
        setImageAlt(attrs.alt || '');
      }
    },
    editorProps: {
      handleDrop: (_view, event, _slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/'));
          if (files.length > 0) {
            event.preventDefault();
            handleImageUpload(files[0]);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              event.preventDefault();
              const file = item.getAsFile();
              if (file) {
                handleImageUpload(file);
              }
              return true;
            }
          }
        }
        return false;
      }
    }
  });

  const handleImageUpload = useCallback(async (file: File) => {
    if (!editor) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const url = await contentBlocksApi.uploadImage(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      editor.chain().focus().setImage({ src: url }).run();

      notifications.show({
        title: t('common.success'),
        message: t('properties.contentBlocks.imageUploaded'),
        color: 'green',
        icon: <IconCheck size={16} />
      });
    } catch (error) {
      console.error('Image upload failed:', error);
      notifications.show({
        title: t('common.error'),
        message: t('properties.contentBlocks.imageUploadError'),
        color: 'red'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [editor, t]);

  const handleMultipleImageUpload = useCallback(async (files: File[]) => {
    const uploadedImages: { url: string; width: number }[] = [];

    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      try {
        setUploadProgress(Math.round((i / files.length) * 100));
        const url = await contentBlocksApi.uploadImage(files[i]);
        uploadedImages.push({ url, width: 100 });
      } catch (error) {
        console.error('Failed to upload image:', error);
      }
    }

    setUploadProgress(100);
    setUploading(false);
    setGalleryImages(prev => [...prev, ...uploadedImages]);
  }, []);

  // Image editing functions
  // IMPORTANT: updateAttributes does NOT trigger onUpdate, so we must manually call onChange
  const updateImageWidth = useCallback((width: number) => {
    if (!editor) return;
    setImageWidth(width);
    editor.chain().focus().updateAttributes('image', { width }).run();
    // onUpdate не срабатывает при изменении атрибутов — вызываем onChange вручную
    onChange(editor.getHTML());
  }, [editor, onChange]);

  const updateImageAlignment = useCallback((alignment: string) => {
    if (!editor) return;
    setImageAlignment(alignment);
    editor.chain().focus().updateAttributes('image', { alignment }).run();
    onChange(editor.getHTML());
  }, [editor, onChange]);

  const updateImageAlt = useCallback((alt: string) => {
    if (!editor) return;
    setImageAlt(alt);
    editor.chain().focus().updateAttributes('image', { alt }).run();
    onChange(editor.getHTML());
  }, [editor, onChange]);

  const deleteSelectedImage = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().deleteSelection().run();
    setImageSelected(false);
    onChange(editor.getHTML());
  }, [editor, onChange]);

  const insertLink = useCallback(() => {
    if (!editor || !linkUrl) return;

    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }

    setLinkUrl('');
    closeLinkModal();
  }, [editor, linkUrl, closeLinkModal]);

  const insertTable = useCallback(() => {
    if (!editor) return;

    editor.chain().focus().insertTable({
      rows: tableRows,
      cols: tableCols,
      withHeaderRow: true
    }).run();

    closeTableModal();
  }, [editor, tableRows, tableCols, closeTableModal]);

  const insertGallery = useCallback(() => {
    if (!editor || galleryImages.length === 0) return;

    const galleryHtml = `
      <div class="image-gallery" data-columns="${galleryColumns}" style="display: grid; grid-template-columns: repeat(${galleryColumns}, 1fr); gap: 10px; margin: 16px 0;">
        ${galleryImages.map(img => `
          <div class="gallery-item" style="position: relative;">
            <img src="${img.url}" alt="" style="width: ${img.width}%; height: auto; border-radius: 8px; object-fit: cover;" />
          </div>
        `).join('')}
      </div>
    `;

    editor.chain().focus().insertContent(galleryHtml).run();

    setGalleryImages([]);
    setGalleryColumns(3);
    closeGalleryModal();
  }, [editor, galleryImages, galleryColumns, closeGalleryModal]);

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({
    icon: Icon,
    label,
    isActive = false,
    onClick,
    disabled = false
  }: {
    icon: React.ComponentType<any>;
    label: string;
    isActive?: boolean;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <Tooltip label={label} withArrow>
      <ActionIcon
        variant={isActive ? 'filled' : 'subtle'}
        color={isActive ? 'blue' : 'gray'}
        onClick={onClick}
        disabled={disabled}
        size="md"
      >
        <Icon size={16} />
      </ActionIcon>
    </Tooltip>
  );

  return (
    <Paper withBorder radius="md" className="rich-text-editor">
      {/* Toolbar */}
      <Box className="editor-toolbar" p="xs">
        <Group gap={4} wrap="wrap">
          {/* Text formatting */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconBold}
              label={t('properties.contentBlocks.editor.bold')}
              isActive={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ToolbarButton
              icon={IconItalic}
              label={t('properties.contentBlocks.editor.italic')}
              isActive={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
              icon={IconUnderline}
              label={t('properties.contentBlocks.editor.underline')}
              isActive={editor.isActive('underline')}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <ToolbarButton
              icon={IconStrikethrough}
              label={t('properties.contentBlocks.editor.strikethrough')}
              isActive={editor.isActive('strike')}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
          </Group>

          <Divider orientation="vertical" />

          {/* Headings */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconH1}
              label={t('properties.contentBlocks.editor.heading1')}
              isActive={editor.isActive('heading', { level: 1 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            />
            <ToolbarButton
              icon={IconH2}
              label={t('properties.contentBlocks.editor.heading2')}
              isActive={editor.isActive('heading', { level: 2 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            />
            <ToolbarButton
              icon={IconH3}
              label={t('properties.contentBlocks.editor.heading3')}
              isActive={editor.isActive('heading', { level: 3 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            />
          </Group>

          <Divider orientation="vertical" />

          {/* Lists */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconList}
              label={t('properties.contentBlocks.editor.bulletList')}
              isActive={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <ToolbarButton
              icon={IconListNumbers}
              label={t('properties.contentBlocks.editor.orderedList')}
              isActive={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
          </Group>

          <Divider orientation="vertical" />

          {/* Alignment */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconAlignLeft}
              label={t('properties.contentBlocks.editor.alignLeft')}
              isActive={editor.isActive({ textAlign: 'left' })}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            />
            <ToolbarButton
              icon={IconAlignCenter}
              label={t('properties.contentBlocks.editor.alignCenter')}
              isActive={editor.isActive({ textAlign: 'center' })}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            />
            <ToolbarButton
              icon={IconAlignRight}
              label={t('properties.contentBlocks.editor.alignRight')}
              isActive={editor.isActive({ textAlign: 'right' })}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            />
            <ToolbarButton
              icon={IconAlignJustified}
              label={t('properties.contentBlocks.editor.alignJustify')}
              isActive={editor.isActive({ textAlign: 'justify' })}
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            />
          </Group>

          <Divider orientation="vertical" />

          {/* Colors */}
          <Group gap={2}>
            <Popover width={250} position="bottom" withArrow>
              <Popover.Target>
                <Tooltip label={t('properties.contentBlocks.editor.textColor')} withArrow>
                  <ActionIcon variant="subtle" color="gray" size="md">
                    <IconTextColor size={16} />
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="xs" fw={500} mb="xs">{t('properties.contentBlocks.editor.textColor')}</Text>
                <SimpleGrid cols={7} spacing={4}>
                  {COLOR_PRESETS.map(color => (
                    <UnstyledButton
                      key={color}
                      onClick={() => editor.chain().focus().setColor(color).run()}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        backgroundColor: color,
                        border: '1px solid #ddd'
                      }}
                    />
                  ))}
                </SimpleGrid>
                <Button
                  size="xs"
                  variant="light"
                  mt="xs"
                  fullWidth
                  onClick={() => editor.chain().focus().unsetColor().run()}
                >
                  {t('common.reset')}
                </Button>
              </Popover.Dropdown>
            </Popover>

            <Popover width={250} position="bottom" withArrow>
              <Popover.Target>
                <Tooltip label={t('properties.contentBlocks.editor.highlight')} withArrow>
                  <ActionIcon
                    variant={editor.isActive('highlight') ? 'filled' : 'subtle'}
                    color={editor.isActive('highlight') ? 'yellow' : 'gray'}
                    size="md"
                  >
                    <IconHighlight size={16} />
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="xs" fw={500} mb="xs">{t('properties.contentBlocks.editor.highlightColor')}</Text>
                <SimpleGrid cols={7} spacing={4}>
                  {COLOR_PRESETS.slice(10).map(color => (
                    <UnstyledButton
                      key={color}
                      onClick={() => editor.chain().focus().setHighlight({ color }).run()}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        backgroundColor: color,
                        border: '1px solid #ddd'
                      }}
                    />
                  ))}
                </SimpleGrid>
                <Button
                  size="xs"
                  variant="light"
                  mt="xs"
                  fullWidth
                  onClick={() => editor.chain().focus().unsetHighlight().run()}
                >
                  {t('common.reset')}
                </Button>
              </Popover.Dropdown>
            </Popover>
          </Group>

          <Divider orientation="vertical" />

          {/* Links */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconLink}
              label={t('properties.contentBlocks.editor.addLink')}
              isActive={editor.isActive('link')}
              onClick={() => {
                const previousUrl = editor.getAttributes('link').href;
                setLinkUrl(previousUrl || '');
                openLinkModal();
              }}
            />
            {editor.isActive('link') && (
              <ToolbarButton
                icon={IconLinkOff}
                label={t('properties.contentBlocks.editor.removeLink')}
                onClick={() => editor.chain().focus().unsetLink().run()}
              />
            )}
          </Group>

          <Divider orientation="vertical" />

          {/* Image & Gallery */}
          <Group gap={2}>
            <Tooltip label={t('properties.contentBlocks.editor.insertImage')} withArrow>
              <ActionIcon variant="subtle" color="gray" size="md" onClick={openImageModal}>
                <IconPhoto size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('properties.contentBlocks.editor.insertGallery')} withArrow>
              <ActionIcon variant="subtle" color="gray" size="md" onClick={openGalleryModal}>
                <IconLayoutGrid size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Divider orientation="vertical" />

          {/* Tables */}
          <Group gap={2}>
            <Tooltip label={t('properties.contentBlocks.editor.insertTable')} withArrow>
              <ActionIcon variant="subtle" color="gray" size="md" onClick={openTableModal}>
                <IconTable size={16} />
              </ActionIcon>
            </Tooltip>

            {editor.isActive('table') && (
              <>
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <Tooltip label={t('properties.contentBlocks.editor.tableOptions')} withArrow>
                      <ActionIcon variant="subtle" color="blue" size="md">
                        <IconSettings size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>{t('properties.contentBlocks.editor.rows')}</Menu.Label>
                    <Menu.Item
                      leftSection={<IconRowInsertTop size={14} />}
                      onClick={() => editor.chain().focus().addRowBefore().run()}
                    >
                      {t('properties.contentBlocks.editor.addRowBefore')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconRowInsertBottom size={14} />}
                      onClick={() => editor.chain().focus().addRowAfter().run()}
                    >
                      {t('properties.contentBlocks.editor.addRowAfter')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => editor.chain().focus().deleteRow().run()}
                    >
                      {t('properties.contentBlocks.editor.deleteRow')}
                    </Menu.Item>

                    <Menu.Divider />

                    <Menu.Label>{t('properties.contentBlocks.editor.columns')}</Menu.Label>
                    <Menu.Item
                      leftSection={<IconColumnInsertLeft size={14} />}
                      onClick={() => editor.chain().focus().addColumnBefore().run()}
                    >
                      {t('properties.contentBlocks.editor.addColumnBefore')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconColumnInsertRight size={14} />}
                      onClick={() => editor.chain().focus().addColumnAfter().run()}
                    >
                      {t('properties.contentBlocks.editor.addColumnAfter')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => editor.chain().focus().deleteColumn().run()}
                    >
                      {t('properties.contentBlocks.editor.deleteColumn')}
                    </Menu.Item>

                    <Menu.Divider />

                    <Menu.Item
                      leftSection={<IconTableMinus size={14} />}
                      color="red"
                      onClick={() => editor.chain().focus().deleteTable().run()}
                    >
                      {t('properties.contentBlocks.editor.deleteTable')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </>
            )}
          </Group>

          <Divider orientation="vertical" />

          {/* Other */}
          <Group gap={2}>
            <ToolbarButton
              icon={IconQuote}
              label={t('properties.contentBlocks.editor.blockquote')}
              isActive={editor.isActive('blockquote')}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            />
            <ToolbarButton
              icon={IconSeparator}
              label={t('properties.contentBlocks.editor.horizontalRule')}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
            />
            <ToolbarButton
              icon={IconClearFormatting}
              label={t('properties.contentBlocks.editor.clearFormatting')}
              onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            />
          </Group>
        </Group>
      </Box>

      {/* Image Editing Toolbar - shows when image is selected */}
      {imageSelected && (
        <Box className="image-toolbar" px="sm" py="xs">
          <Group gap="md" wrap="wrap" align="center">
            {/* Size presets */}
            <Group gap={4}>
              <Text size="xs" fw={500} c="dimmed" mr={4}>
                <IconResize size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {t('properties.contentBlocks.editor.imageSize') || 'Size'}:
              </Text>
              {[25, 50, 75, 100].map(size => (
                <Button
                  key={size}
                  size="compact-xs"
                  variant={imageWidth === size ? 'filled' : 'light'}
                  color={imageWidth === size ? 'blue' : 'gray'}
                  onClick={() => updateImageWidth(size)}
                >
                  {size}%
                </Button>
              ))}
            </Group>

            <Divider orientation="vertical" />

            {/* Width slider */}
            <Box style={{ width: 120 }}>
              <Slider
                size="xs"
                min={10}
                max={100}
                step={5}
                value={imageWidth}
                onChange={updateImageWidth}
                marks={[
                  { value: 25 },
                  { value: 50 },
                  { value: 75 },
                  { value: 100 }
                ]}
              />
            </Box>

            <Divider orientation="vertical" />

            {/* Alignment */}
            <Group gap={4}>
              <Text size="xs" fw={500} c="dimmed" mr={4}>
                {t('properties.contentBlocks.editor.imageAlign') || 'Align'}:
              </Text>
              <Tooltip label={t('properties.contentBlocks.editor.alignLeft')} withArrow>
                <ActionIcon
                  size="sm"
                  variant={imageAlignment === 'left' ? 'filled' : 'subtle'}
                  color={imageAlignment === 'left' ? 'blue' : 'gray'}
                  onClick={() => updateImageAlignment('left')}
                >
                  <IconLayoutAlignLeft size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t('properties.contentBlocks.editor.alignCenter')} withArrow>
                <ActionIcon
                  size="sm"
                  variant={imageAlignment === 'center' ? 'filled' : 'subtle'}
                  color={imageAlignment === 'center' ? 'blue' : 'gray'}
                  onClick={() => updateImageAlignment('center')}
                >
                  <IconLayoutAlignCenter size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t('properties.contentBlocks.editor.alignRight')} withArrow>
                <ActionIcon
                  size="sm"
                  variant={imageAlignment === 'right' ? 'filled' : 'subtle'}
                  color={imageAlignment === 'right' ? 'blue' : 'gray'}
                  onClick={() => updateImageAlignment('right')}
                >
                  <IconLayoutAlignRight size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>

            <Divider orientation="vertical" />

            {/* Alt text */}
            <Popover width={280} position="bottom" withArrow>
              <Popover.Target>
                <Tooltip label={t('properties.contentBlocks.editor.altText') || 'Alt text'} withArrow>
                  <ActionIcon size="sm" variant="subtle" color="gray">
                    <IconTextCaption size={14} />
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <TextInput
                  size="xs"
                  label={t('properties.contentBlocks.editor.altText') || 'Alt text'}
                  placeholder={t('properties.contentBlocks.editor.altTextPlaceholder') || 'Image description...'}
                  value={imageAlt}
                  onChange={(e) => updateImageAlt(e.target.value)}
                />
              </Popover.Dropdown>
            </Popover>

            {/* Full width */}
            <Tooltip label={t('properties.contentBlocks.editor.fullWidth') || 'Full width'} withArrow>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onClick={() => updateImageWidth(100)}
              >
                <IconArrowsMaximize size={14} />
              </ActionIcon>
            </Tooltip>

            {/* Delete image */}
            <Tooltip label={t('common.delete')} withArrow>
              <ActionIcon size="sm" variant="subtle" color="red" onClick={deleteSelectedImage}>
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Box>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Box px="xs" pb="xs">
          <Progress value={uploadProgress} animated size="xs" />
          <Text size="xs" c="dimmed" ta="center" mt={4}>
            {t('properties.contentBlocks.editor.uploading')}... {uploadProgress}%
          </Text>
        </Box>
      )}

      {/* Editor Content */}
      <Box className="editor-content" p="md">
        <EditorContent editor={editor} />
      </Box>

      {/* Link Modal */}
      <Modal opened={linkModalOpened} onClose={closeLinkModal} title={t('properties.contentBlocks.editor.addLink')} size="sm">
        <Stack gap="md">
          <TextInput
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeLinkModal}>{t('common.cancel')}</Button>
            <Button onClick={insertLink}>{t('common.insert')}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Image Upload Modal */}
      <Modal opened={imageModalOpened} onClose={closeImageModal} title={t('properties.contentBlocks.editor.insertImage')} size="md">
        <Stack gap="md">
          <Box
            className="dropzone"
            style={{
              border: '2px dashed #dee2e6',
              borderRadius: 8,
              padding: 40,
              textAlign: 'center',
              cursor: 'pointer'
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#228be6';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = '#dee2e6';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#dee2e6';
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
              if (files.length > 0) {
                handleImageUpload(files[0]);
                closeImageModal();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImageUpload(file);
                  closeImageModal();
                }
              }}
            />
            <ThemeIcon size={48} radius="xl" variant="light" color="blue" mb="md">
              <IconUpload size={24} />
            </ThemeIcon>
            <Text fw={500} mb="xs">{t('properties.contentBlocks.editor.dropImageHere')}</Text>
            <Text size="sm" c="dimmed">{t('properties.contentBlocks.editor.orClickToSelect')}</Text>
          </Box>
          <Text size="xs" c="dimmed" ta="center">
            {t('properties.contentBlocks.editor.supportedFormats')}: JPEG, PNG, GIF, WebP
          </Text>
        </Stack>
      </Modal>

      {/* Table Modal */}
      <Modal opened={tableModalOpened} onClose={closeTableModal} title={t('properties.contentBlocks.editor.insertTable')} size="sm">
        <Stack gap="md">
          <NumberInput
            label={t('properties.contentBlocks.editor.rows')}
            value={tableRows}
            onChange={(val) => setTableRows(Number(val) || 3)}
            min={1}
            max={20}
          />
          <NumberInput
            label={t('properties.contentBlocks.editor.columns')}
            value={tableCols}
            onChange={(val) => setTableCols(Number(val) || 3)}
            min={1}
            max={10}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeTableModal}>{t('common.cancel')}</Button>
            <Button onClick={insertTable}>{t('common.insert')}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Gallery Modal */}
      <Modal
        opened={galleryModalOpened}
        onClose={closeGalleryModal}
        title={t('properties.contentBlocks.editor.insertGallery')}
        size="lg"
      >
        <Stack gap="md">
          <Box
            className="dropzone"
            style={{
              border: '2px dashed #dee2e6',
              borderRadius: 8,
              padding: 30,
              textAlign: 'center',
              cursor: 'pointer'
            }}
            onClick={() => galleryInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#228be6';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = '#dee2e6';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#dee2e6';
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
              if (files.length > 0) {
                handleMultipleImageUpload(files);
              }
            }}
          >
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  handleMultipleImageUpload(files);
                }
              }}
            />
            <ThemeIcon size={40} radius="xl" variant="light" color="blue" mb="sm">
              <IconPhotoPlus size={20} />
            </ThemeIcon>
            <Text fw={500} size="sm">{t('properties.contentBlocks.editor.dropImagesHere')}</Text>
            <Text size="xs" c="dimmed">{t('properties.contentBlocks.editor.selectMultiple')}</Text>
          </Box>

          {uploading && (
            <Progress value={uploadProgress} animated size="sm" />
          )}

          {galleryImages.length > 0 && (
            <>
              <Text size="sm" fw={500}>{t('properties.contentBlocks.editor.selectedImages')}: {galleryImages.length}</Text>
              <SimpleGrid cols={4} spacing="xs">
                {galleryImages.map((img, idx) => (
                  <Box key={idx} style={{ position: 'relative' }}>
                    <MantineImage
                      src={img.url}
                      alt=""
                      radius="sm"
                      h={80}
                      fit="cover"
                    />
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="filled"
                      style={{ position: 'absolute', top: 4, right: 4 }}
                      onClick={() => setGalleryImages(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  </Box>
                ))}
              </SimpleGrid>

              <Select
                label={t('properties.contentBlocks.editor.columns')}
                value={String(galleryColumns)}
                onChange={(val) => setGalleryColumns(Number(val) || 3)}
                data={[
                  { value: '2', label: '2 ' + t('properties.contentBlocks.editor.columns') },
                  { value: '3', label: '3 ' + t('properties.contentBlocks.editor.columns') },
                  { value: '4', label: '4 ' + t('properties.contentBlocks.editor.columns') }
                ]}
              />
            </>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { setGalleryImages([]); closeGalleryModal(); }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={insertGallery}
              disabled={galleryImages.length === 0}
              leftSection={<IconLayoutGrid size={16} />}
            >
              {t('properties.contentBlocks.editor.insertGallery')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
